import smartGeofenceEndpoints from './endpoints/smartGeofence.endpoints';

/**
 * Get smart geofences derived from POL significant locations.
 * @param {number} personId - The person/alien identifier.
 * @returns {Promise<Object|null>} Smart geofences data.
 */
export async function getSmartGeofences(personId) {
  try {
    const response = await smartGeofenceEndpoints.get({ params: { person_id: personId } });
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    if (error.response?.status === 404) {
      console.log(`[getSmartGeofences] No POL model found for person ${personId}`);
      return null;
    }
    console.error('[getSmartGeofences] Error:', error);
    return null;
  }
}

/**
 * Check if a person is compliant with their expected geofence.
 * @param {number} personId - The person/alien identifier.
 * @param {number} latitude - Current latitude.
 * @param {number} longitude - Current longitude.
 * @param {string} [timestamp] - Optional ISO timestamp (defaults to now).
 * @returns {Promise<Object|null>} Compliance result.
 */
export async function checkGeofenceCompliance(personId, latitude, longitude, timestamp = null) {
  try {
    const payload = {
      person_id: personId,
      latitude,
      longitude,
    };
    if (timestamp) {
      payload.timestamp = timestamp;
    }

    const response = await smartGeofenceEndpoints.checkCompliance(payload);
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    if (error.response?.status === 404) {
      console.log(`[checkGeofenceCompliance] No POL model found for person ${personId}`);
      return null;
    }
    console.error('[checkGeofenceCompliance] Error:', error);
    return null;
  }
}

/**
 * Get the geofence type display configuration.
 * @param {string} fenceType - The fence type (home, work, frequent, etc.)
 * @returns {Object} Configuration with icon, color, and label.
 */
export function getGeofenceTypeConfig(fenceType) {
  const configs = {
    home: { icon: '\uD83C\uDFE0', color: '#22C55E', bgColor: '#DCFCE7', label: 'Home' },
    work: { icon: '\uD83C\uDFE2', color: '#3B82F6', bgColor: '#DBEAFE', label: 'Work' },
    frequent: { icon: '\uD83D\uDCCD', color: '#F59E0B', bgColor: '#FEF3C7', label: 'Frequent' },
    restricted: { icon: '\uD83D\uDEAB', color: '#EF4444', bgColor: '#FEE2E2', label: 'Restricted' },
    custom: { icon: '\u2B50', color: '#8B5CF6', bgColor: '#EDE9FE', label: 'Custom' },
  };
  return configs[fenceType] || configs.custom;
}

/**
 * Get compliance status display configuration.
 * @param {string} status - The compliance status.
 * @returns {Object} Configuration with color, bgColor, and label.
 */
export function getComplianceStatusConfig(status) {
  const configs = {
    at_home: { color: '#22C55E', bgColor: '#DCFCE7', label: 'At Home' },
    at_work: { color: '#3B82F6', bgColor: '#DBEAFE', label: 'At Work' },
    at_frequent: { color: '#F59E0B', bgColor: '#FEF3C7', label: 'At Known Location' },
    at_known_location: { color: '#F59E0B', bgColor: '#FEF3C7', label: 'At Known Location' },
    outside_expected: { color: '#EF4444', bgColor: '#FEE2E2', label: 'Outside Expected' },
    outside_all: { color: '#6B7280', bgColor: '#F3F4F6', label: 'Outside All Zones' },
    unexpected_location: { color: '#EF4444', bgColor: '#FEE2E2', label: 'Unexpected Location' },
  };
  return configs[status] || { color: '#6B7280', bgColor: '#F3F4F6', label: status };
}

/**
 * Format active hours for display.
 * @param {Object} activeHours - Active hours configuration.
 * @returns {string} Formatted string.
 */
export function formatActiveHours(activeHours) {
  if (!activeHours) return 'Always Active';

  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const days = activeHours.days || [];
  const dayStr = days.length === 7
    ? 'Every day'
    : days.map(d => dayNames[d]).join(', ');

  return `${activeHours.start} - ${activeHours.end} (${dayStr})`;
}

/**
 * Calculate the area of a circular geofence in square meters.
 * @param {number} radiusMeters - Radius in meters.
 * @returns {number} Area in square meters.
 */
export function calculateGeofenceArea(radiusMeters) {
  return Math.PI * radiusMeters * radiusMeters;
}

/**
 * Format area for display (sq meters or sq km).
 * @param {number} areaSquareMeters - Area in square meters.
 * @returns {string} Formatted area string.
 */
export function formatArea(areaSquareMeters) {
  if (areaSquareMeters >= 1000000) {
    return `${(areaSquareMeters / 1000000).toFixed(2)} km²`;
  }
  return `${Math.round(areaSquareMeters).toLocaleString()} m²`;
}
